// src/pages/VehicleV2/AddRetreadedTyre.jsx

import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaSave, FaTimes, FaUndo, FaArrowLeft } from "react-icons/fa";

const API_BASE = "https://nissi-india.com/api/addReteredTyre";

const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const toStr = (v, def = "") => {
  if (v === undefined || v === null) return def;
  return String(v).trim();
};

const isoToDDMMYYYY = (iso) => {
  if (!iso) return "";
  const txt = String(iso).trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(txt)) return "";
  const [yyyy, mm, dd] = txt.split("-");
  return `${dd}/${mm}/${yyyy}`;
};

const ddmmyyyyToISO = (s) => {
  if (!s) return "";
  const txt = String(s).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(txt)) return txt;
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(txt);
  if (!m) return "";
  return `${m[3]}-${m[2]}-${m[1]}`;
};

const emptyForm = {
  tyreType: "existing",
  dateISO: new Date().toISOString().split("T")[0],
  tyreSizePlyRating: "",
  typeOfRetread: "",
  retreadedDateISO: "",
  tyreAmount: "",
  vehicleId: "select",
  serialId: "Select",
  serialNoText: "",
  position: "",
  noOfRetread: "",
  retreadedFrom: "",
  retreadedFromText: "",
};

const AddRetreadedTyre = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const editId = useMemo(
    () =>
      toInt(
        searchParams.get("retreadTyreId") ||
          searchParams.get("retreadedTyreId") ||
          searchParams.get("RetrededTyreID") ||
          0
      ),
    [searchParams]
  );

  const urlType = useMemo(
    () => toStr(searchParams.get("type")).toUpperCase(),
    [searchParams]
  );

  const loginUserId =
    localStorage.getItem("userId") || localStorage.getItem("loginuserid") || "";
  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "HO";
  const branchId = Number(
    localStorage.getItem("branchId") || localStorage.getItem("branchid") || 8
  );

  const finYear =
    localStorage.getItem("financialYear") ||
    localStorage.getItem("finyear") ||
    localStorage.getItem("finYear") ||
    "";
  const finYearId =
    localStorage.getItem("financialYearId") ||
    localStorage.getItem("finyearid") ||
    localStorage.getItem("finYearId") ||
    localStorage.getItem("financialyearid") ||
    "";

  const [loading, setLoading] = useState(false);
  const [retTyreVI, setRetTyreVI] = useState(0);
  const [vehicleCodes, setVehicleCodes] = useState([]);
  const [serials, setSerials] = useState([]);
  const [retreadFromList, setRetreadFromList] = useState([]);
  const [noOfRetreadList, setNoOfRetreadList] = useState([]);
  const [positions, setPositions] = useState([
    { id: "Front", name: "Front" },
    { id: "Rear", name: "Rear" },
  ]);

  const RETREAD_TYPES = useMemo(
    () => [
      { id: "Hot", name: "Hot" },
      { id: "Cold", name: "Cold" },
    ],
    []
  );

  const [formData, setFormData] = useState(emptyForm);

  const inventoryHeaders = useMemo(() => {
    const headers = { "x-branchid": branchId, "x-userid": loginUserId };
    if (toStr(finYear)) headers["x-finyear"] = toStr(finYear);
    if (toStr(finYearId)) {
      headers["x-finyearid"] = toStr(finYearId);
      headers["x-finyear-id"] = toStr(finYearId);
    }
    return headers;
  }, [finYear, finYearId, branchId, loginUserId]);

  const inputClass = `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
    isDark
      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
  }`;

  const labelClass = `block mb-2 text-sm font-medium ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  const showSerialDropdown = useMemo(() => {
    if (formData.tyreType === "existing") return true;
    return retTyreVI === 1;
  }, [formData.tyreType, retTyreVI]);

  const effectiveSerialNo = useMemo(() => {
    if (showSerialDropdown) {
      const opt = serials.find((x) => String(x.id) === String(formData.serialId));
      return opt ? String(opt.name || "") : "";
    }
    return toStr(formData.serialNoText);
  }, [showSerialDropdown, formData.serialId, formData.serialNoText, serials]);

  const isRetreadFromNew = toStr(formData.retreadedFrom) === "New";

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      if (name === "tyreType" || name === "vehicleId") {
        next.serialId = "Select";
        next.serialNoText = "";
        next.tyreSizePlyRating = "";
        next.position = "";
        next.tyreAmount = "";
        next.noOfRetread = "";
      }
      if (name === "retreadedFrom" && value !== "New") next.retreadedFromText = "";
      return next;
    });
  };

  const renderOptions = (arr, valueKey = "id", labelKey = "name") =>
    (arr || []).map((o) => (
      <option
        key={`${o[valueKey]}`}
        value={String(o[valueKey])}
        disabled={o.enabled === false}
      >
        {String(o[labelKey])}
      </option>
    ));

  const loadSerials = async ({
    vi = retTyreVI,
    vehicleId = 0,
    tyreType = formData.tyreType,
    source,
  } = {}) => {
    const type = tyreType === "existing" ? "E" : "N";

    if (vi === 1) {
      if (!vehicleId) {
        setSerials([]);
        return [];
      }
      const r = await axios.get(`${API_BASE}/tyre-serials`, {
        params: {
          branchId,
          RetTyreVI: 1,
          type,
          vehicleId,
          source: source || "vehicleIssue",
        },
        headers: inventoryHeaders,
      });
      const rows = Array.isArray(r.data?.serials) ? r.data.serials : [];
      setSerials(rows);
      return rows;
    }

    const r = await axios.get(`${API_BASE}/tyre-serials`, {
      params: { branchId, RetTyreVI: 0, type },
      headers: inventoryHeaders,
    });
    const rows = Array.isArray(r.data?.serials) ? r.data.serials : [];
    setSerials(rows);
    return rows;
  };

  const loadSerialDetails = async (serialId, serialNo) => {
    if (!serialNo) return;
    const r = await axios.get(`${API_BASE}/serial-details`, {
      params: { serialId, serialNo },
      headers: inventoryHeaders,
    });
    const data = r.data?.data;
    if (!data) return;

    setFormData((prev) => ({
      ...prev,
      tyreSizePlyRating: data.tyreSize || prev.tyreSizePlyRating,
      position: data.tyrePosition ? String(data.tyrePosition) : prev.position,
      tyreAmount:
        data.amount !== undefined ? String(data.amount || "") : prev.tyreAmount,
    }));

    if (Array.isArray(data.noOfRetreadedRemaining) && data.noOfRetreadedRemaining.length) {
      setNoOfRetreadList(
        data.noOfRetreadedRemaining.map((x) => ({
          value: String(x.value),
          label: String(x.label),
          enabled: x.enabled !== false,
        }))
      );
    }
  };

  const loadBase = async () => {
    const cfg = await axios.get(`${API_BASE}/config`, { headers: inventoryHeaders });
    const vi = toInt(cfg.data?.RetTyreVI, 0);
    setRetTyreVI(vi);

    const dd = await axios.get(`${API_BASE}/dropdowns`, {
      params: { branchId, branchType, RetTyreVI: vi },
      headers: inventoryHeaders,
    });

    setRetreadFromList(Array.isArray(dd.data?.reterdFrom) ? dd.data.reterdFrom : []);
    setNoOfRetreadList(Array.isArray(dd.data?.noOfRetreaded) ? dd.data.noOfRetreaded : []);

    if (Array.isArray(dd.data?.positions) && dd.data.positions.length) {
      setPositions(dd.data.positions);
    }

    const vc = await axios.get(`${API_BASE}/vehicle-codes`, {
      params: { branchId, RetTyreVI: vi, mode: "normal" },
      headers: inventoryHeaders,
    });
    setVehicleCodes(Array.isArray(vc.data?.vehicleCodes) ? vc.data.vehicleCodes : []);
    return vi;
  };

  useEffect(() => {
    const init = async () => {
      try {
        if (!branchId) {
          toast.error("branchId missing");
          return;
        }
        setLoading(true);
        const vi = await loadBase();

        if (editId > 0) {
          const r = await axios.get(`${API_BASE}/view`, {
            params: { retreadedTyreId: editId },
            headers: inventoryHeaders,
          });
          const data = r.data?.data;
          if (!data) {
            toast.error("Record not found");
            return;
          }

          const typeEorN =
            urlType === "E" || urlType === "N"
              ? urlType
              : toStr(data.type || "E").toUpperCase();
          const tyreType = typeEorN === "E" ? "existing" : "new";

          const loadedSerials = await loadSerials({
            vi,
            vehicleId: toInt(data.vehicleId),
            tyreType,
            source: vi === 1 ? "vehicleIssue" : undefined,
          });

          let matchedSerialId = "Select";
          const serialText = toStr(data.tyreSerialNo);
          if (Array.isArray(loadedSerials) && serialText) {
            const match = loadedSerials.find((x) => toStr(x.name) === serialText);
            if (match) matchedSerialId = String(match.id);
          }

          setFormData((prev) => ({
            ...prev,
            tyreType,
            dateISO: ddmmyyyyToISO(data.date) || prev.dateISO,
            tyreSizePlyRating: data.tyreSize || "",
            typeOfRetread: ["Hot", "Cold"].includes(String(data.retreadType))
              ? String(data.retreadType)
              : "",
            retreadedDateISO: ddmmyyyyToISO(data.retreadedDate) || "",
            tyreAmount: data.tyreAmount ? String(data.tyreAmount) : "",
            vehicleId: data.vehicleId ? String(data.vehicleId) : "select",
            serialId: matchedSerialId,
            serialNoText: data.tyreSerialNo || "",
            position: data.tyrePosition ? String(data.tyrePosition) : "",
            noOfRetread: data.noOfRetreaded ? String(data.noOfRetreaded) : "",
            retreadedFrom: data.retreadedFrom || "",
            retreadedFromText: "",
          }));
        } else {
          await loadSerials({ vi, vehicleId: 0, tyreType: formData.tyreType });
        }
      } catch (err) {
        toast.error(err?.response?.data?.message || err.message || "Failed to load");
      } finally {
        setLoading(false);
      }
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId, branchId, branchType, urlType]);

  useEffect(() => {
    const run = async () => {
      try {
        if (retTyreVI !== 1) return;
        const vId = toInt(formData.vehicleId);
        if (!vId) {
          setSerials([]);
          return;
        }
        await loadSerials({ vi: 1, vehicleId: vId, tyreType: formData.tyreType });
      } catch (err) {
        toast.error(err?.response?.data?.message || err.message || "Failed to load serials");
      }
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retTyreVI, formData.vehicleId, formData.tyreType]);

  useEffect(() => {
    const run = async () => {
      try {
        if (!showSerialDropdown) return;
        const sid = String(formData.serialId || "");
        if (!sid || sid === "Select" || sid === "select") return;
        const opt = serials.find((x) => String(x.id) === String(sid));
        const serialNo = opt ? String(opt.name || "") : "";
        if (!serialNo) return;
        await loadSerialDetails(sid, serialNo);
      } catch (err) {
        toast.error(err?.response?.data?.message || err.message || "Failed to load serial details");
      }
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSerialDropdown, formData.serialId]);

  const validate = () => {
    if (!formData.dateISO) return "Date is required";
    if (!formData.tyreSizePlyRating) return "Tyre Size/Ply Rating is required";
    if (!formData.typeOfRetread) return "Type of Retread is required";
    if (!formData.vehicleId || formData.vehicleId === "select") return "Vehicle Code is required";
    if (!effectiveSerialNo) return "Tyre Serial No is required";
    if (!formData.position) return "Position is required";
    if (!formData.noOfRetread) return "No of Retread is required";
    if (!formData.retreadedFrom) return "Retreaded From is required";
    if (isRetreadFromNew && !toStr(formData.retreadedFromText)) return "Enter Retreaded From";
    if (!toStr(formData.tyreAmount)) return "Tyre Amount is required";
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const msg = validate();
    if (msg) {
      toast.error(msg);
      return;
    }

    try {
      setLoading(true);
      const payload = {
        retreadedTyreId: editId > 0 ? editId : 0,
        branchId,
        branchType,
        RetTyreVI: retTyreVI,
        type: formData.tyreType === "existing" ? "E" : "N",
        date: isoToDDMMYYYY(formData.dateISO),
        vehicleId: formData.vehicleId,
        serialId: showSerialDropdown ? formData.serialId : "Select",
        tyreSerialNo: effectiveSerialNo,
        tyreSize: formData.tyreSizePlyRating,
        tyrePosition: formData.position,
        retreadType: formData.typeOfRetread,
        noOfRetreaded: formData.noOfRetread,
        retreadedDate: formData.retreadedDateISO
          ? isoToDDMMYYYY(formData.retreadedDateISO)
          : "",
        retreadedFrom: formData.retreadedFrom,
        retreadedFromText: formData.retreadedFromText,
        tyreAmount: formData.tyreAmount,
        loginUserId,
      };

      const r = await axios.post(`${API_BASE}/save`, payload, { headers: inventoryHeaders });
      if (r.data?.success) {
        toast.success(
          r.data?.message || (editId > 0 ? "Updated Successfully" : "Inserted Successfully")
        );
        navigate("/vehicle/TyreDetailsHome");
      } else {
        toast.error(r.data?.message || "Save failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async () => {
    try {
      if (editId > 0) {
        navigate(0);
        return;
      }
      setSerials([]);
      setFormData({
        ...emptyForm,
        dateISO: new Date().toISOString().split("T")[0],
      });
      const vi = await loadBase();
      await loadSerials({ vi, vehicleId: 0, tyreType: "existing" });
      toast.info("Form has been reset");
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "Reset failed");
    }
  };

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">ADD RETREADED TYRE</span>}
          setSkin={setSkin}
          currentSkin={currentSkin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <form onSubmit={handleSubmit}>
            <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
              <div>
                <h2 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                  {editId > 0 ? "Edit Retreaded Tyre" : "Add Retreaded Tyre"}
                </h2>
                {loading && (
                  <p className={`text-sm mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                    Loading...
                  </p>
                )}
              </div>

              <div className="flex items-center gap-3 flex-wrap">
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg ${
                    isDark
                      ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                      : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                  }`}
                >
                  <FaArrowLeft /> Back
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className={`flex items-center gap-2 px-5 py-3 rounded-lg font-semibold shadow-lg text-white ${
                    loading ? "opacity-60 cursor-not-allowed" : ""
                  } ${buttonClass} ${hoverClass}`}
                >
                  <FaSave />
                  {loading ? "Saving..." : editId > 0 ? "Update" : "Save"}
                </button>
              </div>
            </div>

            <Section title="Tyre Type" isDark={isDark}>
              <div className="flex flex-wrap gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="tyreType"
                    value="existing"
                    checked={formData.tyreType === "existing"}
                    onChange={handleInputChange}
                    className="w-4 h-4 text-blue-600"
                  />
                  Existing Tyre
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="tyreType"
                    value="new"
                    checked={formData.tyreType === "new"}
                    onChange={handleInputChange}
                    className="w-4 h-4 text-blue-600"
                  />
                  New Tyre
                </label>
              </div>
              <p className={`text-xs mt-3 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                Existing = your own casing sent for retread. New = retreaded tyre bought as retread.
              </p>
            </Section>

            <Section title="Tyre Details" isDark={isDark}>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <Field label="Date *" labelClass={labelClass}>
                  <input
                    type="date"
                    name="dateISO"
                    value={formData.dateISO}
                    onChange={handleInputChange}
                    className={inputClass}
                  />
                </Field>

                <Field label="Vehicle Code *" labelClass={labelClass}>
                  <select
                    name="vehicleId"
                    value={formData.vehicleId}
                    onChange={handleInputChange}
                    className={inputClass}
                  >
                    <option value="select">Select</option>
                    {renderOptions(vehicleCodes)}
                  </select>
                </Field>

                <Field label="Tyre Serial No *" labelClass={labelClass}>
                  {showSerialDropdown ? (
                    <select
                      name="serialId"
                      value={formData.serialId}
                      onChange={handleInputChange}
                      className={inputClass}
                    >
                      <option value="Select">Select</option>
                      {renderOptions(serials)}
                    </select>
                  ) : (
                    <input
                      type="text"
                      name="serialNoText"
                      value={formData.serialNoText}
                      onChange={handleInputChange}
                      className={inputClass}
                      placeholder="Enter serial number"
                    />
                  )}
                </Field>

                <Field label="Tyre Size / Ply Rating *" labelClass={labelClass}>
                  <input
                    type="text"
                    name="tyreSizePlyRating"
                    value={formData.tyreSizePlyRating}
                    onChange={handleInputChange}
                    className={inputClass}
                    placeholder="Enter tyre size/ply rating"
                  />
                </Field>

                <Field label="Position *" labelClass={labelClass}>
                  <select
                    name="position"
                    value={formData.position}
                    onChange={handleInputChange}
                    className={inputClass}
                  >
                    <option value="">Select</option>
                    {renderOptions(positions)}
                  </select>
                </Field>

                <Field label="Type of Retread *" labelClass={labelClass}>
                  <select
                    name="typeOfRetread"
                    value={formData.typeOfRetread}
                    onChange={handleInputChange}
                    className={inputClass}
                  >
                    <option value="">Select</option>
                    {renderOptions(RETREAD_TYPES)}
                  </select>
                </Field>
              </div>
            </Section>

            <Section title="Retread Details" isDark={isDark}>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <Field label="No of Retread *" labelClass={labelClass}>
                  <select
                    name="noOfRetread"
                    value={formData.noOfRetread}
                    onChange={handleInputChange}
                    className={inputClass}
                  >
                    <option value="">Select</option>
                    {(noOfRetreadList || []).map((o) => (
                      <option
                        key={String(o.value)}
                        value={String(o.value)}
                        disabled={o.enabled === false}
                      >
                        {o.label}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label="Retreaded Date" labelClass={labelClass}>
                  <input
                    type="date"
                    name="retreadedDateISO"
                    value={formData.retreadedDateISO}
                    onChange={handleInputChange}
                    className={inputClass}
                  />
                </Field>

                <Field label="Tyre Amount *" labelClass={labelClass}>
                  <input
                    type="text"
                    name="tyreAmount"
                    value={formData.tyreAmount}
                    onChange={handleInputChange}
                    className={inputClass}
                    placeholder="Enter amount"
                  />
                </Field>

                <Field label="Retreaded From *" labelClass={labelClass}>
                  <select
                    name="retreadedFrom"
                    value={formData.retreadedFrom}
                    onChange={handleInputChange}
                    className={inputClass}
                  >
                    <option value="">Select</option>
                    {renderOptions(retreadFromList)}
                  </select>
                </Field>

                {isRetreadFromNew && (
                  <Field label="Enter Retreaded From *" labelClass={labelClass}>
                    <input
                      type="text"
                      name="retreadedFromText"
                      value={formData.retreadedFromText}
                      onChange={handleInputChange}
                      className={inputClass}
                      placeholder="Enter retreaded from"
                    />
                  </Field>
                )}
              </div>
            </Section>

            <div className="flex justify-between items-center mt-2 pt-6 border-t border-gray-200 dark:border-gray-700 flex-wrap gap-3">
              <button
                type="button"
                onClick={handleReset}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border ${
                  isDark
                    ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                    : "border-gray-300 text-gray-700 hover:bg-gray-100"
                }`}
              >
                <FaUndo />
                {editId > 0 ? "Reload" : "Reset"}
              </button>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border ${
                    isDark
                      ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                      : "border-gray-300 text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  <FaTimes /> Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg text-white ${
                    loading ? "opacity-60 cursor-not-allowed" : ""
                  } ${buttonClass} ${hoverClass}`}
                >
                  <FaSave />
                  {editId > 0 ? "Update Retreaded Tyre" : "Save Retreaded Tyre"}
                </button>
              </div>
            </div>
          </form>

          <ToastContainer />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

function Section({ title, isDark, children }) {
  return (
    <div className={`p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
      <h3 className={`text-lg font-semibold mb-4 ${isDark ? "text-white" : "text-blue-900"}`}>
        {title}
      </h3>
      {children}
    </div>
  );
}

function Field({ label, labelClass, children }) {
  return (
    <div>
      <label className={labelClass}>{label}</label>
      {children}
    </div>
  );
}

export default AddRetreadedTyre;